import { ImageTransformDefaultsProvider } from '@opallabs/cassini'
import type { ImageTransforms } from '@opallabs/cassini'
import { Post } from '../../Post'
import { SnapshotAnnotationOverlay } from '../SnapshotAnnotationOverlay'
import * as styles from './styles'

// Snapshots are captured offscreen at a 1024px viewport, so no image here is
// displayed above that width — but an ImageProxy asked for no size fetches the
// original upload. Print-resolution photography then takes tens of seconds per
// image, and the capturing browser abandons the page long before it settles,
// which is how content with large assets ends up with no preview at all.
// `inside` keeps the proxy from cropping to the requested box.
const SNAPSHOT_IMAGE_TRANSFORMS: ImageTransforms = {
  fit: 'inside',
  width: 1024,
}

type SnapshotRenderProps = {
  // Any number of props could flow through this component
  [x: string]: unknown
  accountImage: Record<string, unknown>
  accountName: string
  accountUsername: string
  annotationX?: number
  annotationY?: number
  assets?: Array<unknown>
  caption?: string
  content: string
  displays: Array<string>
  filter?: string
  headerImage?: unknown
  location?: string
  modules?: Array<unknown>
  newsFeedImage?: unknown
  notCropped?: boolean
  onDisplayChange?: (...args: Array<unknown>) => unknown
  postType: string
  readOnly?: boolean
  scheduledAt?: string
  service: string
  timezone: string
}

export const SnapshotRender: React.VFC<SnapshotRenderProps> = ({
  accountImage,
  accountName,
  accountUsername,
  annotationX = null,
  annotationY = null,
  assets,
  caption,
  content,
  displays,
  filter = null,
  headerImage = null,
  location = null,
  modules = null,
  newsFeedImage = null,
  notCropped = false,
  onDisplayChange,
  postType,
  readOnly = true,
  scheduledAt = null,
  service,
  timezone,
  ...otherProps // needed for dynamically mapped post options
}) => {
  const hasAnnotation =
    typeof annotationX === 'number' && typeof annotationY === 'number'

  // When an annotation is requested, only show the first display
  const renderableDisplays = hasAnnotation ? [displays[0]] : displays

  return (
    <ImageTransformDefaultsProvider transforms={SNAPSHOT_IMAGE_TRANSFORMS}>
      {renderableDisplays.map((display) => (
        <div
          id={display}
          key={display}
          css={styles.snapshotRender}
          className={`snapshot-render ${display}`}
          style={{ display: 'inline-block' }}
        >
          {hasAnnotation && (
            <SnapshotAnnotationOverlay x={annotationX} y={annotationY} />
          )}
          <Post
            {...otherProps}
            accountImage={accountImage}
            accountName={accountName}
            accountUsername={accountUsername}
            annotationX={annotationX}
            annotationY={annotationY}
            assets={assets}
            caption={caption}
            content={content}
            display={display}
            filter={filter}
            headerImage={headerImage}
            key={display}
            location={location}
            modules={modules}
            newsFeedImage={newsFeedImage}
            notCropped={notCropped}
            onDisplayChange={onDisplayChange}
            postType={postType}
            readOnly={readOnly}
            scheduledAt={scheduledAt}
            service={service}
            timezone={timezone}
          />
        </div>
      ))}
    </ImageTransformDefaultsProvider>
  )
}
